import { Plugin, PluginKey, TextSelection } from "prosemirror-state";
import { EditorView } from "prosemirror-view";

import { schema } from "../adapter";
import { ALLOWED_IMAGE_MIME_TYPES, isAllowedImageType } from "../utils";

import { collaborationUploadFile } from "api/api-worksheets";
import { t } from "i18n/i18n";
import { toastErrorMessage, toastLocalizedOperationCatcher } from "utils/alerts";
import { rollbarAndLogError } from "utils/logger";

/**
 * Display the overlay while image is uploading
 *
 * @param wrapperElement - The wrapper element containing the editor
 * @returns The overlay element, or null if it couldn't be created
 */
function addUploadOverlay(wrapperElement: HTMLElement, count: number): HTMLElement | null {
  let overlayElement = wrapperElement.querySelector<HTMLElement>(".upload-overlay");
  if (!overlayElement) {
    overlayElement = document.createElement("div");
    overlayElement.className =
      "upload-overlay " +
      "animate-fade-out animate-fade-out-trigger " +
      "pointer-events-auto " +
      "absolute bottom-0 left-0 right-0 top-0 " +
      "flex items-center justify-center " +
      "text-white bg-black/70 rounded-md " +
      "z-[1001]"; // Above the toolbar

    const text = document.createElement("p");
    text.className = "body";
    text.textContent = t("worksheet.rich_text_editor.uploading_image", { count });
    overlayElement.appendChild(text);

    wrapperElement.appendChild(overlayElement);
  }

  return overlayElement;
}

export const handleImageUploads = async (view: EditorView, ...files: File[]) => {
  for (const file of files) {
    if (!isAllowedImageType(file.type)) {
      toastErrorMessage(
        t("worksheet.rich_text_editor.file_type_not_supported", {
          types: Object.keys(ALLOWED_IMAGE_MIME_TYPES),
        }),
      );
      return;
    }
  }

  const wrapperElement = view.dom.closest(".prosemirror-editor-wrapper") as HTMLElement | null;
  if (!wrapperElement) {
    rollbarAndLogError("[ProseMirrorEditor] Wrapper element not found for image upload");
    return;
  }

  const overlay = addUploadOverlay(wrapperElement, files.length);

  try {
    // Upload the files
    const responses = await Promise.all(files.map((file) => collaborationUploadFile(file)));

    // Get the current state (may have changed since button was clicked)
    const currentState = view.state;
    const currentFrom = currentState.selection.from;

    // Insert the image at the current selection position
    const imageNodes = responses.map((response) =>
      schema.nodes.image.create({ src: response.url }),
    );
    const tr = currentState.tr.insert(currentFrom, imageNodes);

    // Move cursor after the inserted images (each image node has size 1)
    const newPos = currentFrom + imageNodes.length;
    const resolvedPos = tr.doc.resolve(newPos);
    tr.setSelection(TextSelection.near(resolvedPos));

    view.dispatch(tr);
  } catch (error) {
    rollbarAndLogError("[ProseMirrorEditor] Error uploading image(s)", error);
    toastLocalizedOperationCatcher("image_upload_failure")(error);
  } finally {
    overlay?.remove();
  }
};

function handleDragOver(event: DragEvent) {
  const dataTransfer = event.dataTransfer;
  if (!dataTransfer) return false;
  if (dataTransfer.types.includes("Files")) {
    event.preventDefault();
  }
}

function handleDrop(view: EditorView, event: DragEvent) {
  const dataTransfer = event.dataTransfer;
  if (!dataTransfer) return false;
  // Allowlist enforcement (JPEG/PNG/GIF/WebP) happens in handleImageUploads.
  const files = [...dataTransfer.items].flatMap((item) => {
    if (item.kind !== "file") return [];
    const file = item.getAsFile();
    return file ? [file] : [];
  });
  if (files.length > 0) {
    // Move the selection to the drop position
    const dropPos =
      view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos ??
      view.state.selection.from;
    const tr = view.state.tr.setSelection(TextSelection.near(view.state.doc.resolve(dropPos)));
    view.dispatch(tr);

    handleImageUploads(view, ...files);
    event.preventDefault();
    return true;
  }
}

export function imageDropPlugin(): Plugin {
  return new Plugin({
    key: new PluginKey("image-drop"),

    // According to the ProseMirror documentation, we *should* be able to attach these event handlers
    // through `props.handleDOMEvents`, but it doesn't seem to be working.
    // As a workaround, we add a native event listeners directly to the editor's DOM element when the plugin is initialized.
    view(editorView) {
      const dropHandler = (event: DragEvent) => handleDrop(editorView, event);

      editorView.dom.addEventListener("dragover", handleDragOver);
      editorView.dom.addEventListener("drop", dropHandler);
      return {
        destroy() {
          editorView.dom.removeEventListener("dragover", handleDragOver);
          editorView.dom.removeEventListener("drop", dropHandler);
        },
      };
    },
  });
}
